import React, { useState, useEffect } from 'react';
import { List, ListItem, ListItemButton, ListItemText, ListItemIcon, Divider, Box, Typography, AppBar, Toolbar, IconButton, Switch, FormControlLabel, MenuItem, Select, Chip, Avatar, Drawer } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '../icons';
import { Close as CloseIcon } from '../icons';
import { AdminPanelSettings as AdminPanelSettingsIcon } from '../icons';
import { Visibility } from '../icons';
import { VisibilityOff } from '../icons';
import { useNavigate } from 'react-router-dom';
import { auth, db, functions, signInWithGoogle } from '../firebase';
import { endSession } from '../session';
import { isIOSApp } from '../utils/device';
import { serverTimestamp } from 'firebase/firestore';
import firebase from 'firebase/compat/app';
import { showsContact, setContactVisibility } from '../utils/contactPrivacy';
import {
  getAuth,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  TotpMultiFactorGenerator,
} from 'firebase/auth';
import { QRCodeSVG } from 'qrcode.react';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { isNativePasskeyBridgeAvailable, nativeStartPasskeyRegistration } from '../utils/nativePasskey';
import { rememberPasskeyForEmail, forgetPasskeyForEmail } from '../utils/passkeyDevice';
import {
  generateSecret,
  generateBackupCodes,
  sha256Hex,
  verifyTotp,
  buildOtpAuthUri,
} from '../utils/totp';
import { fetchBlockedIds, unblockUser } from '../utils/blockedUsers';
import { isAdminUser } from '../utils/adminAccess';
import { pageContentSx, pageHeaderInnerSx } from '../pageLayout';
import { brandColors, barColors } from '../theme';
import SheetDragHandle from '../components/SheetDragHandle';

const SECTIONS = [
  {
    key: 'dosPasos',
    label: 'Verificación en dos pasos',
    description: 'Protegé tu cuenta con un código extra desde una app autenticadora o por SMS.'
  },
  {
    key: 'correo',
    label: 'Cambiar correo electrónico',
    description: 'Actualiza tu dirección de correo electrónico.'
  },
  {
    key: 'contraseña',
    label: 'Cambiar contraseña',
    description: 'Actualizá la contraseña de tu cuenta.'
  },
  {
    key: 'passkeys',
    label: 'Llaves de acceso (passkeys)',
    description: 'Iniciá sesión sin contraseña usando tu huella, rostro o PIN del dispositivo.'
  },
  {
    key: 'cuentas',
    label: 'Gestionar cuentas vinculadas',
    description: 'Vincula o desvincula tus cuentas de Google.'
  },
  {
    key: 'verificacionCuenta',
    label: 'Verificación de cuenta',
    description: 'Obtén la insignia de cuenta verificada para tu marca o empresa.'
  },
  {
    key: 'privacidad',
    label: 'Preferencias de privacidad',
    description: 'Controla quién puede ver tu perfil y tu actividad.'
  },
  {
    key: 'notificaciones',
    label: 'Notificaciones',
    description: 'Elegí qué notificaciones querés recibir.'
  },
  {
    key: 'tema',
    label: 'Tema de la aplicación',
    description: 'Elige entre Gris Oscuro, Modo Oscuro, Modo Claro o Azul.'
  },
  {
    key: 'ubicacion',
    label: 'Configurar ubicación o zona horaria',
    description: 'Actualiza tu ubicación o selecciona tu zona horaria preferida.'
  },
  {
    key: 'anuncios',
    label: 'Preferencias de anuncios',
    description: 'Personaliza el tipo de anuncios o contenido que ves.'
  },
  {
    key: 'historial',
    label: 'Historial de actividad o sesiones activas',
    description: 'Consulta tu historial de actividad y gestiona sesiones abiertas.'
  },
  {
    key: 'bloqueados',
    label: 'Usuarios bloqueados',
    description: 'Gestiona las cuentas que bloqueaste.'
  },
  {
    key: 'eliminar',
    label: 'Eliminar cuenta',
    description: 'Elimina tu cuenta de forma permanente.'
  },
];

const Settings = () => {
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();
  // Evita el crash reportado en Crashlytics: si `selected` no coincide con
  // ninguna sección (ej. una key vieja que ya no existe), no accedemos
  // directamente a .label/.description de un valor undefined.
  const selectedSection = SECTIONS.find(s => s.key === selected);

  return (
    <Box>
      <AppBar position="fixed" sx={{ backgroundColor: barColors.background, color: barColors.text }}>
        <Toolbar disableGutters sx={{ p: '10px' }}>
          <Box sx={pageHeaderInnerSx}>
            <IconButton color="inherit" onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ color: barColors.text, ml: 2, fontWeight: 'bold' }}>
              Ajustes de Perfil
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Box sx={{ ...pageContentSx, mt: 8, mb: 10 }}>
        <List>
          {SECTIONS.filter((section) => !(isIOSApp() && section.key === 'cuentas')).map((section, idx) => (
            <React.Fragment key={section.key}>
              <ListItem disablePadding>
                <ListItemButton onClick={() => setSelected(section.key)}>
                  <ListItemText
                    primary={section.label}
                    secondary={section.description}
                    primaryTypographyProps={{ fontWeight: 'bold' }}
                  />
                </ListItemButton>
              </ListItem>
              {idx < SECTIONS.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
        <Drawer
          anchor="bottom"
          open={!!selected}
          onClose={() => setSelected(null)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '85vh',
              maxWidth: '980px',
              left: 0,
              right: 0,
              mx: 'auto',
            },
          }}
        >
          {selectedSection && (
            <Box sx={{ pt: 1.5, pb: 4, overflowY: 'auto' }}>
              <SheetDragHandle onClose={() => setSelected(null)} />
              <Box sx={{ ...pageContentSx, px: { xs: 3, md: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {selectedSection.label}
                  </Typography>
                  <IconButton onClick={() => setSelected(null)} size="small" sx={{ mt: -0.5, mr: -1 }}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Typography sx={{ mb: 2, color: 'text.secondary' }}>{selectedSection.description}</Typography>
                {selected === 'cuentas' && (
                  <GestionarCuentas />
                )}
                {selected === 'privacidad' && (
                  <PreferenciasPrivacidad />
                )}
                {selected === 'eliminar' && (
                  <EliminarCuenta />
                )}
                {selected === 'ubicacion' && (
                  <ConfigurarUbicacion />
                )}
                {selected === 'anuncios' && (
                  <PreferenciasAnuncios />
                )}
                {selected === 'correo' && (
                  <CambiarCorreo />
                )}
                {selected === 'contraseña' && (
                  <CambiarContraseña />
                )}
                {selected === 'passkeys' && (
                  <GestionarPasskeys />
                )}
                {selected === 'tema' && (
                  <PreferenciasTema />
                )}
                {selected === 'historial' && (
                  <HistorialActividad />
                )}
                {selected === 'dosPasos' && (
                  <>
                    <VerificacionDosPasos />
                    <Divider sx={{ my: 3 }} />
                    <VerificacionSms />
                  </>
                )}
                {selected === 'verificacionCuenta' && (
                  <VerificacionCuenta />
                )}
                {selected === 'notificaciones' && (
                  <Notificaciones />
                )}
                {selected === 'bloqueados' && (
                  <UsuariosBloqueados />
                )}
              </Box>
            </Box>
          )}
        </Drawer>
        {isAdminUser(auth.currentUser) && (
          <>
            <Divider />
            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={() => navigate('/admin')}>
                  <ListItemIcon><AdminPanelSettingsIcon /></ListItemIcon>
                  <ListItemText primary="Panel admin" />
                </ListItemButton>
              </ListItem>
            </List>
          </>
        )}
      </Box>
    </Box>
  );
};


// Componentes internos para cada sección
function GestionarCuentas() {
  const [linked, setLinked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setLinked(user.providerData.some(p => p.providerId === 'google.com'));
    }
    setLoading(false);
  }, []);

  const handleLink = async () => {
    setError('');
    try {
      await signInWithGoogle();
      setLinked(true);
    } catch (e) {
      setError('Error al vincular Google: ' + e.message);
    }
  };

  const handleUnlink = async () => {
    setError('');
    try {
      await auth.currentUser.unlink('google.com');
      setLinked(false);
    } catch (e) {
      setError('Error al desvincular Google: ' + e.message);
    }
  };

  if (loading) return <Typography>Cargando...</Typography>;

  return (
    <Box>
      <Typography sx={{ mb: 2 }}>Gestiona tu cuenta de Google vinculada.</Typography>
      {linked ? (
        <button onClick={handleUnlink} style={{ color: 'white', background: '#1976d2', padding: '8px 16px', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Desvincular Google</button>
      ) : (
        <button onClick={handleLink} style={{ color: 'white', background: '#1976d2', padding: '8px 16px', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Vincular Google</button>
      )}
      {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
    </Box>
  );
}

// Cada switch de PreferenciasPrivacidad guarda directo en users/{uid} y
// muestra su propio texto de ayuda debajo, igual que en producción.
const PRIVACY_TOGGLES = [
  {
    field: 'perfilPublico',
    label: 'Perfil público',
    helper: 'Si lo desactivás, solo vos podés ver tu perfil y tu ubicación en el mapa.',
    default: true,
  },
  {
    field: 'cuentaPrivada',
    label: 'Cuenta privada',
    // Ya consumido por utils/follow.js (toggleFollow) y members.js para
    // decidir si seguir a alguien manda una solicitud en vez de seguir
    // directo — hasta ahora no había forma de activarlo desde la UI.
    helper: 'Si la activás, quien quiera seguirte te manda una solicitud y vos la aceptás o rechazás desde tus notificaciones.',
    default: false,
  },
  {
    field: 'mostrarSeguidores',
    label: 'Mostrar mis seguidores y seguidos',
    helper: 'Controla si otras personas pueden ver tus listas y contadores de seguidores/seguidos en tu perfil.',
    default: true,
  },
  {
    field: 'apareceEnBusqueda',
    label: 'Aparecer en el directorio de miembros',
    // Ya consumido por members.js (filtro apareceEnBusqueda !== false) sin
    // forma de desactivarlo desde la UI.
    helper: 'Si lo desactivás, no aparecés en la lista de miembros de la app.',
    default: true,
  },
  {
    field: 'mostrarEnMapa',
    label: 'Mostrar mi estado en el mapa',
    helper: 'Controla si tu ubicación y estado aparecen en el mapa de usuarios activos.',
    default: true,
  },
  {
    field: 'mostrarContacto',
    label: 'Mostrar mi email y teléfono en mi perfil',
    // Si está apagado, el email y el teléfono se guardan en un documento que
    // solo leés vos (ver utils/contactPrivacy.js). Sin elegir, las empresas
    // los muestran y el resto no (el valor real lo pone showsContact al cargar).
    helper: 'Si lo desactivás, nadie más puede ver tu email ni tu teléfono.',
    default: false,
  },
];

// Nombres de campo y valores ('todos'/'seguidos'/'nadie') alineados con
// user.js (/user/:userId) — un profile view alternativo, no enlazado desde
// ningún lado de la app pero con la lógica de permisos ya escrita
// (puedeEnviarMensaje) — para que este ajuste sirva para algo real en vez de
// guardar un campo que nada lee.
const MENSAJE_OPTIONS = [
  { value: 'todos', label: 'Todos' },
  { value: 'seguidos', label: 'Solo a quienes sigo' },
  { value: 'nadie', label: 'Nadie' },
];

function PreferenciasPrivacidad() {
  const [prefs, setPrefs] = useState(() =>
    Object.fromEntries(PRIVACY_TOGGLES.map((t) => [t.field, t.default]))
  );
  const [mensajesDe, setMensajesDe] = useState('todos');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [blockedCount, setBlockedCount] = useState(0);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    db.doc(`users/${user.uid}`).get().then(doc => {
      if (doc.exists) {
        const data = doc.data();
        setPrefs((prev) => {
          const next = { ...prev };
          PRIVACY_TOGGLES.forEach((t) => {
            if (data[t.field] !== undefined) next[t.field] = data[t.field];
          });
          next.mostrarContacto = showsContact(data);
          return next;
        });
        if (data.mensajesDe !== undefined) setMensajesDe(data.mensajesDe);
      }
      setLoading(false);
    });
    fetchBlockedIds(user.uid).then((ids) => setBlockedCount(ids.length));
  }, []);

  const savePref = async (field, value) => {
    setMsg('');
    const user = auth.currentUser;
    if (!user) return;
    if (field === 'mostrarContacto' && value && prefs.perfilPublico === false) {
      setPrefs((prev) => ({ ...prev, mostrarContacto: false }));
      setMsg('Para mostrar tu email y teléfono primero activá "Perfil público".');
      return;
    }
    if (field === 'mostrarContacto') {
      // Además del ajuste, mueve el email y el teléfono al doc público o al privado.
      await setContactVisibility(user.uid, value);
      setMsg('Guardado');
      return;
    }
    if (field === 'perfilPublico' && value === false) {
      // Perfil no público: el contacto pasa a privado antes de guardar el
      // ajuste (la regla no deja dejarlo público con el perfil oculto).
      await setContactVisibility(user.uid, false);
      setPrefs((prev) => ({ ...prev, mostrarContacto: false }));
    }
    // Ocultarse del mapa (o el perfil) también borra la última ubicación
    // guardada: users/{uid} lo puede leer cualquier usuario con sesión.
    const clearLocation = (field === 'mostrarEnMapa' || field === 'perfilPublico') && value === false
      ? {
          latitude: firebase.firestore.FieldValue.delete(),
          longitude: firebase.firestore.FieldValue.delete(),
          accuracy: firebase.firestore.FieldValue.delete(),
        }
      : {};
    await db.doc(`users/${user.uid}`).set({ [field]: value, ...clearLocation }, { merge: true });
    setMsg('Guardado');
  };

  const handleToggle = (field) => async (e) => {
    setPrefs((prev) => ({ ...prev, [field]: e.target.checked }));
    await savePref(field, e.target.checked);
  };

  const handleMensajeChange = async (e) => {
    setMensajesDe(e.target.value);
    await savePref('mensajesDe', e.target.value);
  };

  const handleExport = async () => {
    const user = auth.currentUser;
    if (!user) return;
    setExporting(true);
    try {
      const userSnap = await db.doc(`users/${user.uid}`).get();
      const [offersSnap, eventsSnap, carsSnap, placesSnap] = await Promise.all([
        db.collection('offers').where('uid', '==', user.uid).get(),
        db.collection('events').where('uid', '==', user.uid).get(),
        db.collection('cars').where('uid', '==', user.uid).get(),
        db.collection('places').where('uid', '==', user.uid).get(),
      ]);
      const payload = {
        exportedAt: new Date().toISOString(),
        profile: userSnap.exists ? userSnap.data() : null,
        offers: offersSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
        events: eventsSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
        cars: carsSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
        places: placesSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `notiline-datos-${user.uid}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMsg('Error al exportar tus datos: ' + e.message);
    }
    setExporting(false);
  };

  if (loading) return <Typography>Cargando...</Typography>;
  return (
    <Box>
      {PRIVACY_TOGGLES.map((t) => (
        <Box key={t.field} sx={{ mb: 1.5 }}>
          <FormControlLabel
            control={<Switch checked={prefs[t.field]} onChange={handleToggle(t.field)} />}
            label={t.label}
          />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t.helper}
          </Typography>
        </Box>
      ))}

      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
        Quién puede enviarte mensajes
      </Typography>
      <Select
        value={mensajesDe}
        onChange={handleMensajeChange}
        size="small"
        sx={{ minWidth: 180, mb: 1.5 }}
      >
        {MENSAJE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
        ))}
      </Select>

      {msg && <Typography color="success.main">{msg}</Typography>}
      <Divider sx={{ my: 2 }} />
      <Typography sx={{ fontWeight: 'bold', mb: 0.5 }}>Cuentas bloqueadas</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
        {blockedCount === 0 ? 'No bloqueaste a ninguna cuenta.' : `Bloqueaste ${blockedCount} cuenta(s). Gestionalas en "Usuarios bloqueados".`}
      </Typography>
      <Typography sx={{ fontWeight: 'bold', mb: 1 }}>Tus datos</Typography>
      <button onClick={handleExport} disabled={exporting} style={{ background: 'transparent', border: '1px solid #1976d2', color: '#1976d2', padding: '8px 16px', borderRadius: 4, cursor: 'pointer' }}>
        {exporting ? 'Exportando...' : 'Exportar mis datos'}
      </button>
    </Box>
  );
}

// Colores reales usados por el script de arranque en public/index.html
// (variables CSS --app-bg / --app-fg leídas desde localStorage.preferredThemeMode),
// para que la miniatura de cada tema sea fiel a lo que se ve en la app.
const THEME_PREVIEWS = {
  auto: { label: 'Automático', bg: '#121212', fg: '#ffffff', bar: '#030553' },
  graphite: { label: 'Grafito', bg: '#121212', fg: '#ffffff', bar: '#030553' },
  dark: { label: 'Oscuro', bg: '#0a0a0a', fg: '#ffffff', bar: '#000000' },
  light: { label: 'Claro', bg: '#f2f3f7', fg: '#15171f', bar: '#ffffff' },
  blue: { label: 'Azul', bg: '#10152f', fg: '#ffffff', bar: '#030553' },
};
const THEME_STORAGE_KEY = 'preferredThemeMode';

function ThemeThumbnail({ mode }) {
  const preview = THEME_PREVIEWS[mode];
  // 'Automático' no tiene una paleta fija (sigue al sistema), así que la
  // miniatura muestra mitad Claro / mitad Grafito para transmitir eso.
  if (mode === 'auto') {
    const graphite = THEME_PREVIEWS.graphite;
    const light = THEME_PREVIEWS.light;
    return (
      <Box
        sx={{
          width: 56,
          height: 40,
          borderRadius: 1,
          overflow: 'hidden',
          border: `1px solid ${brandColors.divider}`,
          display: 'flex',
          flexShrink: 0,
        }}
      >
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ height: 10, background: graphite.bar }} />
          <Box sx={{ flex: 1, background: graphite.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ width: 12, height: 4, borderRadius: 2, background: graphite.fg, opacity: 0.85 }} />
          </Box>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ height: 10, background: light.bar, borderBottom: '1px solid rgba(0,0,0,0.12)' }} />
          <Box sx={{ flex: 1, background: light.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ width: 12, height: 4, borderRadius: 2, background: light.fg, opacity: 0.85 }} />
          </Box>
        </Box>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        width: 56,
        height: 40,
        borderRadius: 1,
        overflow: 'hidden',
        border: `1px solid ${brandColors.divider}`,
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}
    >
      <Box sx={{ height: 10, background: preview.bar, borderBottom: preview.bar === '#ffffff' ? '1px solid rgba(0,0,0,0.12)' : 'none' }} />
      <Box sx={{ flex: 1, background: preview.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ width: 30, height: 4, borderRadius: 2, background: preview.fg, opacity: 0.85 }} />
      </Box>
    </Box>
  );
}

function PreferenciasTema() {
  const [mode, setMode] = useState(() => {
    try {
      return window.localStorage.getItem(THEME_STORAGE_KEY) || 'auto';
    } catch (e) {
      return 'auto';
    }
  });

  const handleSelect = (value) => {
    if (value === mode) return;
    setMode(value);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, value);
    } catch (e) {
      // localStorage puede no estar disponible (modo privado, etc.)
    }
    // Recarga para que la app tome el tema nuevo, mismo patrón que el
    // selector de idioma.
    window.location.reload();
  };

  return (
    <Box>
      <Typography sx={{ mb: 1.5 }}>Elige el tema visual de la aplicación:</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {Object.keys(THEME_PREVIEWS).map((value) => {
          const preview = THEME_PREVIEWS[value];
          const isSelected = mode === value;
          return (
            <Box
              key={value}
              onClick={() => handleSelect(value)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSelect(value); }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                p: 1,
                borderRadius: 2,
                cursor: 'pointer',
                border: isSelected ? '2px solid #1976d2' : '2px solid transparent',
                background: isSelected ? 'rgba(25,118,210,0.12)' : 'transparent',
              }}
            >
              <ThemeThumbnail mode={value} />
              <Typography variant="caption">{preview.label}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function EliminarCuenta() {
  const [confirm, setConfirm] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [error, setError] = useState('');
  const handleDelete = async () => {
    setError('');
    try {
      await auth.currentUser.delete();
      setDeleted(true);
    } catch (e) {
      setError('Error al eliminar la cuenta: ' + e.message);
    }
  };
  if (deleted) return <Typography color="error">Cuenta eliminada.</Typography>;
  return (
    <Box>
      {!confirm ? (
        <>
          <Typography color="error">¿Estás seguro de que deseas eliminar tu cuenta? Esta acción es irreversible.</Typography>
          <Box sx={{ mt: 2 }}>
            <button onClick={() => setConfirm(true)} style={{ color: 'white', background: '#d32f2f', padding: '8px 16px', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Eliminar cuenta</button>
          </Box>
        </>
      ) : (
        <>
          <Typography color="error">Confirma que deseas eliminar tu cuenta.</Typography>
          <Box sx={{ mt: 2 }}>
            <button onClick={handleDelete} style={{ color: 'white', background: '#d32f2f', padding: '8px 16px', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Confirmar eliminación</button>
          </Box>
          {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
        </>
      )}
    </Box>
  );
}

function ConfigurarUbicacion() {
  // Detecta automáticamente la zona horaria real del usuario
  const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  const [zona, setZona] = useState('');
  const [msg, setMsg] = useState('');
  const zonas = [
    'America/Mexico_City',
    'America/Bogota',
    'America/Argentina/Buenos_Aires',
    'Europe/Madrid',
    'America/Caracas',
    'America/Lima',
    'America/Santiago',
    'America/Guatemala',
    'America/Panama',
    'America/La_Paz',
    'America/Montevideo',
    'America/Santo_Domingo',
    'America/Havana',
    'America/Puerto_Rico',
    'America/Asuncion',
    'America/San_Juan',
    'America/Managua',
    'America/El_Salvador',
    'America/Tegucigalpa',
    'America/Costa_Rica',
    'America/Guayaquil',
    'America/Port-au-Prince',
    'America/Kingston',
    'America/Belize',
    'America/Paramaribo',
    'America/Port_of_Spain',
    'America/Curacao',
    'America/Aruba',
    'America/Barbados',
    'America/Grand_Turk',
    'America/St_Thomas',
    'America/St_Lucia',
    'America/St_Vincent',
    'America/Grenada',
    'America/Antigua',
    'America/Dominica',
    'America/Martinique',
    'America/Guadeloupe',
    'America/Cayman',
    'America/Bermuda',
    'America/Nassau',
    'America/Anguilla',
    'America/Monterrey',
    'America/Tijuana',
    'America/Chihuahua',
    'America/Hermosillo',
    'America/Merida',
    'America/Cancun',
    'America/Mazatlan',
    'America/Ojinaga',
    'America/Matamoros',
    'America/Veracruz',
    'America/Ensenada',
    'America/Acapulco',
    'America/Guadalajara',
    'America/Culiacan',
    'America/Monclova',
    'America/Monterrey',
    'America/Morelia',
    'America/Piedras_Negras',
    'America/Reynosa',
    'America/Torreon',
    'America/Villahermosa',
    'America/Zacatecas',
  ];
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    db.doc(`users/${user.uid}`).get().then(doc => {
      if (doc.exists && doc.data().zona) {
        setZona(doc.data().zona);
      } else if (detectedTimezone) {
        setZona(detectedTimezone);
      }
    });
  }, [detectedTimezone]);
  const handleChange = async (e) => {
    setZona(e.target.value);
    setMsg('');
    const user = auth.currentUser;
    if (!user) return;
    await db.doc(`users/${user.uid}`).set({ zona: e.target.value }, { merge: true });
    setMsg('Guardado');
  };
  return (
    <Box>
      <Typography sx={{ mb: 1 }}>Selecciona tu zona horaria:</Typography>
      <Select value={zona} onChange={handleChange} displayEmpty sx={{ background: brandColors.inputBackground, color: brandColors.textPrimary, mb: 2, minWidth: 220 }}>
        <MenuItem value=""><em>Selecciona una zona</em></MenuItem>
        {zonas.map(z => (
          <MenuItem key={z} value={z}>
            {z}
            {z === detectedTimezone ? ' (Detectada)' : ''}
          </MenuItem>
        ))}
      </Select>
      {msg && <Typography color="success.main">{msg}</Typography>}
      {zona !== detectedTimezone && detectedTimezone && (
        <Typography sx={{ mt: 1, color: brandColors.textSecondary, fontSize: 13 }}>
          Zona horaria detectada automáticamente: <b>{detectedTimezone}</b>
        </Typography>
      )}
    </Box>
  );
}

function PreferenciasAnuncios() {
  const [tipo, setTipo] = useState('general');
  const [msg, setMsg] = useState('');
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    db.doc(`users/${user.uid}`).get().then(doc => {
      if (doc.exists && doc.data().tipoAnuncio) setTipo(doc.data().tipoAnuncio);
    });
  }, []);
  const handleChange = async (e) => {
    setTipo(e.target.value);
    setMsg('');
    const user = auth.currentUser;
    if (!user) return;
    await db.doc(`users/${user.uid}`).set({ tipoAnuncio: e.target.value }, { merge: true });
    setMsg('Guardado');
  };
  return (
    <Box>
      <Typography sx={{ mb: 1 }}>Tipo de anuncios preferido:</Typography>
      <Select value={tipo} onChange={handleChange} sx={{ background: brandColors.inputBackground, color: brandColors.textPrimary, mb: 2, minWidth: 220 }}>
        <MenuItem value="general">General</MenuItem>
        <MenuItem value="personalizado">Personalizado</MenuItem>
        <MenuItem value="ninguno">Ninguno</MenuItem>
      </Select>
      {msg && <Typography color="success.main">{msg}</Typography>}
    </Box>
  );
}

function CambiarCorreo() {
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const user = auth.currentUser;
    if (user) setEmail(user.email || '');
  }, []);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await auth.currentUser.updateEmail(email);
      setSuccess(true);
    } catch (e) {
      setError('Error al actualizar el correo: ' + e.message);
    }
    setLoading(false);
  };
  if (success) return <Typography color="success.main">Correo actualizado correctamente.</Typography>;
  return (
    <Box>
      <form onSubmit={handleSubmit}>
        <input
          type="email"
          placeholder="Nuevo correo electrónico"
          value={email}
          onChange={e => setEmail(e.target.value)}
          style={{ padding: 8, borderRadius: 4, border: '1px solid #ccc', width: '100%', marginBottom: 8, boxSizing: 'border-box' }}
          required
        />
        <button type="submit" disabled={loading} style={{ color: 'white', background: '#1976d2', padding: '8px 16px', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Actualizar correo</button>
      </form>
      {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
    </Box>
  );
}

function CambiarContraseña() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 6) {
      setError('La nueva contraseña debe tener al menos 6 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas nuevas no coinciden.');
      return;
    }
    setLoading(true);
    try {
      const user = getAuth().currentUser;
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e) {
      if (e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential') {
        setError('La contraseña actual es incorrecta.');
      } else {
        setError('No se pudo cambiar la contraseña: ' + e.message);
      }
    }
    setLoading(false);
  };

  if (success) return <Typography color="success.main">Contraseña actualizada correctamente.</Typography>;

  const inputStyle = {
    padding: 8,
    paddingRight: 36,
    borderRadius: 4,
    border: `1px solid ${brandColors.divider}`,
    width: '100%',
    marginBottom: 8,
    boxSizing: 'border-box',
    backgroundColor: brandColors.inputBackground,
    color: brandColors.textPrimary,
  };
  const fieldType = showPasswords ? 'text' : 'password';

  return (
    <Box>
      <form onSubmit={handleSubmit}>
        <Box sx={{ position: 'relative', mb: 1 }}>
          <input
            type={fieldType}
            placeholder="Contraseña actual"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            style={{ ...inputStyle, marginBottom: 0 }}
            autoComplete="current-password"
            required
          />
          <IconButton
            size="small"
            onClick={() => setShowPasswords((v) => !v)}
            sx={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', color: brandColors.textSecondary }}
          >
            {showPasswords ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
          </IconButton>
        </Box>
        <input
          type={fieldType}
          placeholder="Nueva contraseña"
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
          style={inputStyle}
          autoComplete="new-password"
          minLength={6}
          required
        />
        <input
          type={fieldType}
          placeholder="Confirmar nueva contraseña"
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          style={inputStyle}
          autoComplete="new-password"
          minLength={6}
          required
        />
        <button type="submit" disabled={loading} style={{ color: 'white', background: '#1976d2', padding: '8px 16px', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
          {loading ? 'Actualizando...' : 'Actualizar contraseña'}
        </button>
      </form>
      {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
    </Box>
  );
}


function GestionarPasskeys() {
  const [loading, setLoading] = useState(true);
  const [passkeys, setPasskeys] = useState([]);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const supported = browserSupportsWebAuthn() || isNativePasskeyBridgeAvailable();

  const loadPasskeys = async () => {
    setLoading(true);
    try {
      const list = functions.httpsCallable('listPasskeys');
      const { data } = await list();
      setPasskeys(data.passkeys || []);
    } catch (e) {
      setError('No se pudieron cargar tus llaves de acceso: ' + e.message);
    }
    setLoading(false);
  };

  useEffect(() => { loadPasskeys(); }, []);

  const handleRegister = async () => {
    setError('');
    setMsg('');
    setRegistering(true);
    try {
      const getOptions = functions.httpsCallable('generatePasskeyRegistrationOptions');
      const { data: optionsJSON } = await getOptions();
      const registrationResponse = isNativePasskeyBridgeAvailable()
        ? await nativeStartPasskeyRegistration(optionsJSON)
        : await startRegistration({ optionsJSON });

      const verify = functions.httpsCallable('verifyPasskeyRegistration');
      await verify({ response: registrationResponse, label: 'Llave de acceso' });

      rememberPasskeyForEmail(auth.currentUser?.email);
      setMsg('Llave de acceso agregada correctamente.');
      await loadPasskeys();
    } catch (e) {
      if (e.name === 'InvalidStateError') {
        setError('Este dispositivo ya tiene una llave de acceso registrada para esta cuenta.');
      } else if (e.name === 'NotAllowedError') {
        setError('Registro de llave de acceso cancelado.');
      } else {
        setError('No se pudo registrar la llave de acceso: ' + e.message);
      }
    }
    setRegistering(false);
  };

  const handleDelete = async (credentialId) => {
    setError('');
    setMsg('');
    try {
      const del = functions.httpsCallable('deletePasskey');
      await del({ credentialId });
      setPasskeys((prev) => {
        const next = prev.filter((p) => p.id !== credentialId);
        if (next.length === 0) forgetPasskeyForEmail(auth.currentUser?.email);
        return next;
      });
    } catch (e) {
      setError('No se pudo eliminar la llave de acceso: ' + e.message);
    }
  };

  if (!supported) {
    return <Typography>Tu navegador no soporta llaves de acceso (passkeys).</Typography>;
  }

  if (loading) return <Typography>Cargando...</Typography>;

  return (
    <Box>
      <Typography sx={{ mb: 2 }}>
        Las llaves de acceso reemplazan tu contraseña por la huella, el rostro o el PIN de tu dispositivo.
      </Typography>
      {passkeys.length === 0 ? (
        <Typography sx={{ mb: 2, color: 'text.secondary' }}>No tenés llaves de acceso registradas.</Typography>
      ) : (
        <List disablePadding sx={{ mb: 2 }}>
          {passkeys.map((p) => (
            <ListItem
              key={p.id}
              disablePadding
              sx={{ py: 0.75 }}
              secondaryAction={
                <button onClick={() => handleDelete(p.id)} style={{ background: 'transparent', border: '1px solid #d32f2f', color: '#d32f2f', padding: '6px 12px', borderRadius: 4, cursor: 'pointer' }}>
                  Eliminar
                </button>
              }
            >
              <ListItemText
                primary={p.label}
                secondary={p.createdAt ? `Agregada el ${new Date(p.createdAt).toLocaleDateString()}` : null}
              />
            </ListItem>
          ))}
        </List>
      )}
      <button onClick={handleRegister} disabled={registering} style={{ color: 'white', background: '#1976d2', padding: '8px 16px', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
        {registering ? 'Registrando...' : 'Agregar llave de acceso'}
      </button>
      {msg && <Typography color="success.main" sx={{ mt: 1 }}>{msg}</Typography>}
      {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
    </Box>
  );
}

const explainMfaError = (err) => {
  switch (err.code) {
    case 'auth/requires-recent-login':
      return 'Por seguridad, cerrá sesión y volvé a entrar, y después intentá de nuevo.';
    case 'auth/unverified-email':
      return 'Primero tenés que verificar tu email.';
    case 'auth/unsupported-first-factor':
      return 'Esto no se puede hacer si entraste con una llave de acceso. Cerrá sesión y entrá con tu email y contraseña.';
    case 'auth/invalid-phone-number':
      return 'El número no es válido. Escribilo con el código de país, por ejemplo +54 9 11 1234 5678.';
    case 'auth/invalid-verification-code':
      return 'El código no es correcto.';
    case 'auth/code-expired':
      return 'El código expiró. Pedí uno nuevo.';
    case 'auth/too-many-requests':
      return 'Demasiados intentos. Probá de nuevo más tarde.';
    default:
      return err.message;
  }
};

// Códigos de respaldo del 2FA nativo: sirven para entrar sin la app ni el SMS
// (ver useMfaBackupCode). Los genera el servidor; acá solo se ve cuántos quedan.
function MfaBackupCodes() {
  const [count, setCount] = useState(null);
  const [revealed, setRevealed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    db.doc(`users/${auth.currentUser.uid}/private/mfaBackupCodes`).get().then((snap) => {
      const codes = snap.exists ? snap.data().codes || [] : [];
      setCount(codes.filter((c) => !c.used).length);
    });
  }, []);

  const generate = async () => {
    setError('');
    setBusy(true);
    try {
      const { data } = await functions.httpsCallable('generateMfaBackupCodes')();
      setRevealed(data.codes);
      setCount(data.codes.length);
    } catch (err) {
      setError(err.message);
    }
    setBusy(false);
  };

  if (count === null) return null;
  return (
    <Box sx={{ mt: 2 }}>
      {revealed && (
        <Box sx={{ mb: 2, p: 2, border: `1px solid ${brandColors.divider}`, borderRadius: 2, background: brandColors.backgroundPaper }}>
          <Typography sx={{ mb: 1, fontWeight: 'bold' }}>Guardá estos códigos de respaldo — no se van a volver a mostrar:</Typography>
          <Box sx={{ fontFamily: 'monospace', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
            {revealed.map((c) => <span key={c}>{c}</span>)}
          </Box>
          <button onClick={() => setRevealed(null)} style={{ marginTop: 12, background: 'transparent', border: '1px solid #666', color: 'inherit', padding: '6px 12px', borderRadius: 4, cursor: 'pointer' }}>Listo</button>
        </Box>
      )}
      {count === 0 ? (
        <Typography color="error" sx={{ mb: 1 }}>No tenés códigos de respaldo: si perdés el acceso, no vas a poder entrar.</Typography>
      ) : (
        <Typography sx={{ mb: 1 }}>Códigos de respaldo: {count} disponibles</Typography>
      )}
      <button onClick={generate} disabled={busy} style={{ background: 'transparent', border: '1px solid #1976d2', color: '#1976d2', padding: '8px 16px', borderRadius: 4, cursor: 'pointer' }}>
        {count === 0 ? 'Generar códigos de respaldo' : 'Generar nuevos códigos de respaldo'}
      </button>
      {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
    </Box>
  );
}

// App autenticadora. Con contraseña se enrola como factor nativo de Firebase
// (lo exige el servidor, y MfaChallenge deja elegir entre la app y el SMS).
// La versión vieja, propia de la app (TwoFactorChallenge), queda para quien ya
// la tiene y para cuentas sin contraseña, que no podrían usar useMfaBackupCode.
function VerificacionDosPasos() {
  const [loading, setLoading] = useState(true);
  const [nativeFactor, setNativeFactor] = useState(null);
  const [pendingTotp, setPendingTotp] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [addedAt, setAddedAt] = useState(null);
  const [backupCodesCount, setBackupCodesCount] = useState(0);
  const [mode, setMode] = useState('summary'); // summary | setup | confirmDisable
  const [pendingSecret, setPendingSecret] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [setupError, setSetupError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [revealedCodes, setRevealedCodes] = useState(null);

  const loadState = async () => {
    const user = auth.currentUser;
    if (!user) return;
    const snap = await db.doc(`users/${user.uid}`).get();
    const data = snap.exists ? snap.data() : {};
    setNativeFactor(user.multiFactor.enrolledFactors.find((f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID) || null);
    setEnabled(!!data.twoFactorEnabled);
    setAddedAt(data.twoFactorAddedAt || null);
    setBackupCodesCount(Array.isArray(data.twoFactorBackupCodes) ? data.twoFactorBackupCodes.length : 0);
    setLoading(false);
  };

  useEffect(() => { loadState(); }, []);

  const startSetup = async () => {
    setCodeInput('');
    setSetupError('');
    setPendingTotp(null);
    if (!auth.currentUser.providerData.some((p) => p.providerId === 'password')) {
      setPendingSecret(generateSecret());
      setMode('setup');
      return;
    }
    setVerifying(true);
    try {
      const session = await auth.currentUser.multiFactor.getSession();
      const secret = await TotpMultiFactorGenerator.generateSecret(session);
      setPendingTotp(secret);
      setPendingSecret(secret.secretKey);
      setMode('setup');
    } catch (err) {
      setSetupError(explainMfaError(err));
    }
    setVerifying(false);
  };

  const confirmNativeSetup = async () => {
    setSetupError('');
    setVerifying(true);
    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(pendingTotp, codeInput.trim());
      await auth.currentUser.multiFactor.enroll(assertion, 'App autenticadora');
      // El secreto viejo estaba en users/{uid}, legible por cualquier usuario con sesión.
      await db.doc(`users/${auth.currentUser.uid}`).set({
        twoFactorEnabled: false,
        twoFactorSecret: null,
        twoFactorBackupCodes: [],
      }, { merge: true });
      setPendingTotp(null);
      setMode('summary');
      await loadState();
    } catch (err) {
      setSetupError(explainMfaError(err));
    }
    setVerifying(false);
  };

  const confirmSetup = async () => {
    if (pendingTotp) {
      await confirmNativeSetup();
      return;
    }
    setSetupError('');
    setVerifying(true);
    try {
      const isValid = await verifyTotp(pendingSecret, codeInput);
      if (!isValid) {
        setSetupError('El código no es válido. Revisá la hora de tu dispositivo e intentá de nuevo.');
        setVerifying(false);
        return;
      }
      const user = auth.currentUser;
      const codes = generateBackupCodes(10);
      const hashedCodes = await Promise.all(codes.map((c) => sha256Hex(c)));
      await db.doc(`users/${user.uid}`).set({
        twoFactorEnabled: true,
        twoFactorSecret: pendingSecret,
        twoFactorBackupCodes: hashedCodes,
        twoFactorAddedAt: serverTimestamp(),
      }, { merge: true });
      setRevealedCodes(codes);
      setMode('summary');
      await loadState();
    } catch (e) {
      setSetupError('Error al activar la verificación en dos pasos: ' + e.message);
    }
    setVerifying(false);
  };

  const regenerateCodes = async () => {
    const user = auth.currentUser;
    if (!user) return;
    const codes = generateBackupCodes(10);
    const hashedCodes = await Promise.all(codes.map((c) => sha256Hex(c)));
    await db.doc(`users/${user.uid}`).set({ twoFactorBackupCodes: hashedCodes }, { merge: true });
    setRevealedCodes(codes);
    await loadState();
  };

  const disable = async () => {
    const user = auth.currentUser;
    if (!user) return;
    if (nativeFactor) {
      try {
        await user.multiFactor.unenroll(nativeFactor);
        setSetupError('');
      } catch (err) {
        setSetupError(explainMfaError(err));
      }
      setMode('summary');
      await loadState();
      return;
    }
    await db.doc(`users/${user.uid}`).set({
      twoFactorEnabled: false,
      twoFactorSecret: null,
      twoFactorBackupCodes: [],
    }, { merge: true });
    setRevealedCodes(null);
    setMode('summary');
    await loadState();
  };

  if (loading) return <Typography>Cargando...</Typography>;

  if (mode === 'setup') {
    const user = auth.currentUser;
    const otpUri = pendingTotp
      ? pendingTotp.generateQrCodeUrl(user?.email || 'usuario', 'Notiline')
      : buildOtpAuthUri(pendingSecret, user?.email || 'usuario');
    return (
      <Box>
        <Typography sx={{ mb: 2 }}>Escaneá este código con tu app autenticadora (Google Authenticator, Authy, etc.) o ingresá la clave manualmente.</Typography>
        <Box sx={{ background: '#fff', display: 'inline-block', p: 2, borderRadius: 2, mb: 2 }}>
          <QRCodeSVG value={otpUri} size={180} />
        </Box>
        <Typography sx={{ mb: 2, fontFamily: 'monospace', wordBreak: 'break-all' }}>{pendingSecret}</Typography>
        <input
          type="text"
          inputMode="numeric"
          placeholder="Código de 6 dígitos"
          value={codeInput}
          onChange={(e) => setCodeInput(e.target.value)}
          style={{ padding: 8, borderRadius: 4, border: '1px solid #ccc', width: 200, marginBottom: 8, display: 'block' }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <button onClick={confirmSetup} disabled={verifying || codeInput.length < 6} style={{ color: 'white', background: '#1976d2', padding: '8px 16px', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
            Confirmar y activar
          </button>
          <button onClick={() => setMode('summary')} style={{ background: 'transparent', border: '1px solid #666', color: 'inherit', padding: '8px 16px', borderRadius: 4, cursor: 'pointer' }}>
            Cancelar
          </button>
        </Box>
        {setupError && <Typography color="error" sx={{ mt: 1 }}>{setupError}</Typography>}
      </Box>
    );
  }

  if (mode === 'confirmDisable') {
    return (
      <Box>
        <Typography color="error" sx={{ mb: 2 }}>¿Seguro que querés desactivar la verificación en dos pasos? Vas a perder los códigos de respaldo generados.</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <button onClick={disable} style={{ color: 'white', background: '#d32f2f', padding: '8px 16px', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Desactivar verificación en dos pasos</button>
          <button onClick={() => setMode('summary')} style={{ background: 'transparent', border: '1px solid #666', color: 'inherit', padding: '8px 16px', borderRadius: 4, cursor: 'pointer' }}>Cancelar</button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {revealedCodes && (
        <Box sx={{ mb: 2, p: 2, border: `1px solid ${brandColors.divider}`, borderRadius: 2, background: brandColors.backgroundPaper }}>
          <Typography sx={{ mb: 1, fontWeight: 'bold' }}>Guardá estos códigos de respaldo — no se van a volver a mostrar:</Typography>
          <Box sx={{ fontFamily: 'monospace', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
            {revealedCodes.map((c) => <span key={c}>{c}</span>)}
          </Box>
          <button onClick={() => setRevealedCodes(null)} style={{ marginTop: 12, background: 'transparent', border: '1px solid #666', color: 'inherit', padding: '6px 12px', borderRadius: 4, cursor: 'pointer' }}>Listo</button>
        </Box>
      )}
      {mode === 'summary' && setupError && <Typography color="error" sx={{ mb: 1 }}>{setupError}</Typography>}
      {nativeFactor ? (
        <Box>
          <Chip label="Activada" color="success" size="small" sx={{ mb: 1.5 }} />
          <Typography sx={{ mb: 2 }}>
            App autenticadora (agregada el {new Date(nativeFactor.enrollmentTime).toLocaleDateString()})
          </Typography>
          <button onClick={() => setMode('confirmDisable')} style={{ color: 'white', background: '#1976d2', padding: '8px 16px', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
            Desactivar app autenticadora
          </button>
          <MfaBackupCodes />
        </Box>
      ) : enabled ? (
        <Box>
          <Chip label="Activada" color="success" size="small" sx={{ mb: 1.5 }} />
          <Typography sx={{ mb: 1 }}>
            App autenticadora: App autenticadora
            {addedAt?.toDate ? ` (agregada el ${addedAt.toDate().toLocaleDateString()})` : ''}
          </Typography>
          <Typography sx={{ mb: 2 }}>Códigos de respaldo: {backupCodesCount} disponibles</Typography>
          {auth.currentUser.providerData.some((p) => p.providerId === 'password') && (
            <Box sx={{ mb: 2 }}>
              <Typography color="error" sx={{ mb: 1 }}>Tu app autenticadora usa un sistema viejo y menos seguro. Volvé a registrarla: vas a escanear un QR nuevo.</Typography>
              <button onClick={startSetup} disabled={verifying} style={{ color: 'white', background: '#1976d2', padding: '8px 16px', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                Actualizar app autenticadora
              </button>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <button onClick={regenerateCodes} style={{ background: 'transparent', border: '1px solid #1976d2', color: '#1976d2', padding: '8px 16px', borderRadius: 4, cursor: 'pointer' }}>
              Generar nuevos códigos de respaldo
            </button>
            <button onClick={() => setMode('confirmDisable')} style={{ color: 'white', background: '#1976d2', padding: '8px 16px', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
              Desactivar verificación en dos pasos
            </button>
          </Box>
        </Box>
      ) : (
        <Box>
          <Chip label="Desactivada" size="small" sx={{ mb: 1.5 }} />
          <Box>
            <button onClick={startSetup} style={{ color: 'white', background: '#1976d2', padding: '8px 16px', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
              Activar verificación en dos pasos
            </button>
          </Box>
        </Box>
      )}
    </Box>
  );
}

// Código por SMS: segundo factor NATIVO de Firebase Auth (el que pide
// MfaChallenge.js al iniciar sesión), distinto de la app autenticadora de
// arriba, que es propia de la app. Firebase exige el email verificado y un
// login reciente para enrolar. Va con la API compat (auth.currentUser.multiFactor,
// firebase.auth.*): la sesión es de firebase/compat y multiFactor() del SDK
// modular no reconoce ese usuario (tira auth/internal-error y la pantalla
// quedaba en blanco).
const smsButtonSx = { color: 'white', background: '#1976d2', padding: '8px 16px', border: 'none', borderRadius: 4, cursor: 'pointer' };
const smsSecondaryButtonSx = { background: 'transparent', border: '1px solid #666', color: 'inherit', padding: '8px 16px', borderRadius: 4, cursor: 'pointer' };
const smsInputSx = { padding: 8, borderRadius: 4, border: '1px solid #ccc', width: 220, marginBottom: 8, display: 'block' };

function VerificacionSms() {
  const [factors, setFactors] = useState([]);
  const [mode, setMode] = useState('summary'); // summary | phone | code | confirmDisable
  const [phone, setPhone] = useState('+54 9 ');
  const [code, setCode] = useState('');
  const [verificationId, setVerificationId] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const recaptchaRef = React.useRef(null);
  const recaptchaVerifierRef = React.useRef(null);

  const user = auth.currentUser;
  const loadFactors = () => {
    const u = auth.currentUser;
    setFactors(u ? u.multiFactor.enrolledFactors.filter((f) => f.factorId === firebase.auth.PhoneMultiFactorGenerator.FACTOR_ID) : []);
  };
  useEffect(loadFactors, []);

  // El reCAPTCHA invisible se crea cuando aparece el contenedor (paso de
  // ingresar el teléfono) y se descarta al salir de ese paso.
  useEffect(() => {
    if (mode !== 'phone' || !recaptchaRef.current) return undefined;
    recaptchaVerifierRef.current = new firebase.auth.RecaptchaVerifier(recaptchaRef.current, { size: 'invisible' });
    return () => {
      recaptchaVerifierRef.current?.clear();
      recaptchaVerifierRef.current = null;
    };
  }, [mode]);

  const sendCode = async () => {
    setError('');
    setLoading(true);
    try {
      const session = await auth.currentUser.multiFactor.getSession();
      const id = await new firebase.auth.PhoneAuthProvider(auth).verifyPhoneNumber(
        { phoneNumber: phone.replace(/[^\d+]/g, ''), session },
        recaptchaVerifierRef.current
      );
      setVerificationId(id);
      setCode('');
      setMode('code');
    } catch (err) {
      setError(explainMfaError(err));
    }
    setLoading(false);
  };

  const confirmCode = async () => {
    setError('');
    setLoading(true);
    try {
      const credential = firebase.auth.PhoneAuthProvider.credential(verificationId, code.trim());
      await auth.currentUser.multiFactor.enroll(firebase.auth.PhoneMultiFactorGenerator.assertion(credential), 'SMS');
      setMsg('Listo: cuando inicies sesión te vamos a pedir un código por SMS.');
      setMode('summary');
      loadFactors();
    } catch (err) {
      setError(explainMfaError(err));
    }
    setLoading(false);
  };

  const disable = async () => {
    setError('');
    setLoading(true);
    try {
      for (const f of factors) {
        await auth.currentUser.multiFactor.unenroll(f);
      }
      setMsg('Se desactivó el código por SMS.');
      setMode('summary');
      loadFactors();
    } catch (err) {
      setError(explainMfaError(err));
    }
    setLoading(false);
  };

  const sendVerificationEmail = async () => {
    setError('');
    try {
      await functions.httpsCallable('sendVerificationEmail')();
      setMsg('Te mandamos un mail para verificar tu email. Después de verificarlo, cerrá sesión y volvé a entrar.');
    } catch (err) {
      setError(err.message);
    }
  };

  if (!user) return null;
  const hasPassword = user.providerData.some((p) => p.providerId === 'password');

  let body;
  if (mode === 'phone') {
    body = (
      <Box>
        <Typography sx={{ mb: 1 }}>Ingresá tu número de celular con el código de país:</Typography>
        <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} style={smsInputSx} />
        <div ref={recaptchaRef} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <button onClick={sendCode} disabled={loading || phone.replace(/\D/g, '').length < 8} style={smsButtonSx}>
            {loading ? 'Enviando...' : 'Enviar código'}
          </button>
          <button onClick={() => setMode('summary')} style={smsSecondaryButtonSx}>Cancelar</button>
        </Box>
      </Box>
    );
  } else if (mode === 'code') {
    body = (
      <Box>
        <Typography sx={{ mb: 1 }}>Ingresá el código que te llegó por SMS:</Typography>
        <input type="text" inputMode="numeric" autoComplete="one-time-code" placeholder="Código de 6 dígitos"
          value={code} onChange={(e) => setCode(e.target.value)} style={smsInputSx} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <button onClick={confirmCode} disabled={loading || code.trim().length < 6} style={smsButtonSx}>
            {loading ? 'Verificando...' : 'Confirmar y activar'}
          </button>
          <button onClick={() => setMode('phone')} style={smsSecondaryButtonSx}>Cambiar número</button>
        </Box>
      </Box>
    );
  } else if (mode === 'confirmDisable') {
    body = (
      <Box>
        <Typography color="error" sx={{ mb: 2 }}>¿Seguro que querés desactivar el código por SMS?</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <button onClick={disable} disabled={loading} style={{ ...smsButtonSx, background: '#d32f2f' }}>Desactivar SMS</button>
          <button onClick={() => setMode('summary')} style={smsSecondaryButtonSx}>Cancelar</button>
        </Box>
      </Box>
    );
  } else if (factors.length > 0) {
    body = (
      <Box>
        <Chip label="Activado" color="success" size="small" sx={{ mb: 1.5 }} />
        <Typography sx={{ mb: 2 }}>Número: {factors[0].phoneNumber}</Typography>
        <button onClick={() => { setMsg(''); setMode('confirmDisable'); }} style={smsButtonSx}>Desactivar SMS</button>
        {!user.multiFactor.enrolledFactors.some((f) => f.factorId === TotpMultiFactorGenerator.FACTOR_ID) && <MfaBackupCodes />}
      </Box>
    );
  } else if (!hasPassword) {
    body = <Typography sx={{ color: brandColors.textSecondary }}>Disponible para cuentas que inician sesión con email y contraseña.</Typography>;
  } else if (!user.emailVerified) {
    body = (
      <Box>
        <Typography sx={{ mb: 1 }}>Para activar el SMS primero tenés que verificar tu email.</Typography>
        <button onClick={sendVerificationEmail} style={smsButtonSx}>Enviar mail de verificación</button>
      </Box>
    );
  } else {
    body = (
      <Box>
        <Chip label="Desactivado" size="small" sx={{ mb: 1.5 }} />
        <Box>
          <button onClick={() => { setMsg(''); setError(''); setMode('phone'); }} style={smsButtonSx}>Activar código por SMS</button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontWeight: 'bold', mb: 0.5 }}>Código por SMS</Typography>
      <Typography sx={{ mb: 2, color: brandColors.textSecondary }}>
        Al iniciar sesión podés recibir un código en tu celular.
      </Typography>
      {body}
      {msg && <Typography color="success.main" sx={{ mt: 1 }}>{msg}</Typography>}
      {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
    </Box>
  );
}

function VerificacionCuenta() {
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [requested, setRequested] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    db.doc(`users/${user.uid}`).get().then((doc) => {
      if (doc.exists) {
        setVerified(!!doc.data().verified);
        setRequested(!!doc.data().verificationRequested);
      }
      setLoading(false);
    });
  }, []);

  const handleRequest = async () => {
    const user = auth.currentUser;
    if (!user) return;
    await db.doc(`users/${user.uid}`).set({
      verificationRequested: true,
      verificationRequestedAt: serverTimestamp(),
    }, { merge: true });
    setRequested(true);
    setMsg('Solicitud enviada. La revisaremos pronto.');
  };

  const handleCancel = async () => {
    const user = auth.currentUser;
    if (!user) return;
    await db.doc(`users/${user.uid}`).set({ verified: false }, { merge: true });
    setVerified(false);
    setMsg('Verificación cancelada.');
  };

  if (loading) return <Typography>Cargando...</Typography>;

  if (verified) {
    return (
      <Box sx={{ p: 2, border: `1px solid ${brandColors.divider}`, borderRadius: 2, background: brandColors.backgroundPaper, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontWeight: 'bold' }}>✓ Cuenta verificada</Typography>
        <Chip label="Activo" size="small" color="info" variant="outlined" />
        <button onClick={handleCancel} style={{ background: 'transparent', border: '1px solid #d32f2f', color: '#d32f2f', padding: '6px 12px', borderRadius: 4, cursor: 'pointer', width: '100%', boxSizing: 'border-box' }}>
          Cancelar verificación
        </button>
      </Box>
    );
  }

  return (
    <Box>
      {requested ? (
        <Typography sx={{ mb: 1 }}>Tu solicitud de verificación está en revisión.</Typography>
      ) : (
        <button onClick={handleRequest} style={{ color: 'white', background: '#1976d2', padding: '8px 16px', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
          Solicitar verificación
        </button>
      )}
      {msg && <Typography color="success.main" sx={{ mt: 1 }}>{msg}</Typography>}
    </Box>
  );
}

const NOTIFICATION_TOGGLES = [
  { key: 'push', label: 'Notificaciones push', description: 'Recibí avisos en este dispositivo aunque no tengas Notiline abierto.' },
  { key: 'followers', label: 'Nuevos seguidores', description: 'Cuando alguien empieza a seguirte.' },
  { key: 'messages', label: 'Mensajes y pedidos', description: 'Nuevos mensajes directos y pedidos en tu tienda.' },
  { key: 'offers', label: 'Ofertas', description: 'Cuando una cuenta que seguís publica una nueva oferta.' },
  { key: 'events', label: 'Eventos', description: 'Cuando una cuenta que seguís publica un nuevo evento público.' },
  { key: 'followedItems', label: 'Publicaciones que seguís', description: 'Cuando falta poco para un evento u oferta que seguís, o baja de precio.' },
  { key: 'storeActivity', label: 'Actividad de tiendas', description: 'Confirmaciones sobre tu tienda (por ejemplo, cuando se crea).' },
  { key: 'nearby', label: 'Cerca tuyo', description: 'Cuando se publica un evento o abre una tienda cerca de tu ubicación en el mapa.' },
];

const DEFAULT_NOTIFICATION_PREFS = NOTIFICATION_TOGGLES.reduce((acc, t) => ({ ...acc, [t.key]: true }), {});

function Notificaciones() {
  const [prefs, setPrefs] = useState(DEFAULT_NOTIFICATION_PREFS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    db.doc(`users/${user.uid}`).get().then((doc) => {
      if (doc.exists && doc.data().notificationPrefs) {
        setPrefs({ ...DEFAULT_NOTIFICATION_PREFS, ...doc.data().notificationPrefs });
      }
      setLoading(false);
    });
  }, []);

  const handleToggle = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    const user = auth.currentUser;
    if (!user) return;
    await db.doc(`users/${user.uid}`).set({ notificationPrefs: next }, { merge: true });
  };

  if (loading) return <Typography>Cargando...</Typography>;

  return (
    <Box>
      {NOTIFICATION_TOGGLES.map((t, i) => (
        <React.Fragment key={t.key}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.25, gap: 2 }}>
            <Box>
              <Typography sx={{ fontWeight: 'bold' }}>{t.label}</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t.description}</Typography>
            </Box>
            <Switch checked={!!prefs[t.key]} onChange={() => handleToggle(t.key)} />
          </Box>
          {i < NOTIFICATION_TOGGLES.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </Box>
  );
}

function UsuariosBloqueados() {
  const [loading, setLoading] = useState(true);
  const [blockedUsers, setBlockedUsers] = useState([]);

  const load = async () => {
    const user = auth.currentUser;
    if (!user) return;
    setLoading(true);
    const ids = await fetchBlockedIds(user.uid);
    const profiles = await Promise.all(ids.map(async (uid) => {
      const snap = await db.doc(`users/${uid}`).get();
      return { uid, ...(snap.exists ? snap.data() : {}) };
    }));
    setBlockedUsers(profiles);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleUnblock = async (targetUid) => {
    const user = auth.currentUser;
    if (!user) return;
    await unblockUser(user.uid, targetUid);
    setBlockedUsers((prev) => prev.filter((u) => u.uid !== targetUid));
  };

  if (loading) return <Typography>Cargando...</Typography>;

  if (blockedUsers.length === 0) {
    return <Typography>No bloqueaste a ningún usuario.</Typography>;
  }

  return (
    <List disablePadding>
      {blockedUsers.map((u) => (
        <ListItem
          key={u.uid}
          disablePadding
          sx={{ py: 0.75 }}
          secondaryAction={
            <button onClick={() => handleUnblock(u.uid)} style={{ background: 'transparent', border: '1px solid #1976d2', color: '#1976d2', padding: '6px 12px', borderRadius: 4, cursor: 'pointer' }}>
              Desbloquear
            </button>
          }
        >
          <ListItemIcon>
            <Avatar src={u.profileImageUrl} sx={{ width: 32, height: 32 }} />
          </ListItemIcon>
          <ListItemText primary={u.displayName || u.name || 'Usuario'} />
        </ListItem>
      ))}
    </List>
  );
}

function HistorialActividad() {
  const [userData, setUserData] = useState(null);
  const [accesses, setAccesses] = useState([]);
  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setUserData({
        email: user.email,
        lastLogin: user.metadata.lastSignInTime,
        created: user.metadata.creationTime,
        providers: user.providerData.map(p => p.providerId).join(', '),
      });
      // Leer últimos 10 accesos desde Firestore
      db.collection('users').doc(user.uid).collection('accessLogs')
        .orderBy('timestamp', 'desc').limit(10).get()
        .then(snap => {
          setAccesses(snap.docs.map(doc => doc.data()));
        });
    }
  }, []);
  if (!userData) return <Typography>Cargando...</Typography>;

  const handleCerrarOtrasSesiones = async () => {
  try {
    const user = auth.currentUser;
    if (!user) { alert("No estás logueado"); return; }

    const token = await user.getIdToken(true);

    // Llamada directa con fetch, enviando el token manualmente
    const response = await fetch(
      'https://us-central1-notiline-41ce0.cloudfunctions.net/revokeOtherSessions',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`  // ← token explícito
        },
        body: JSON.stringify({})
      }
    );

    if (!response.ok) throw new Error(`Error ${response.status}`);

    alert("Sesiones cerradas");
    await endSession();
    window.location.reload();

  } catch (error) {
    console.error("ERROR:", error);
    alert(error.message);
  }
};
  return (
    <Box>
      <Typography sx={{ mb: 1 }}>Correo: {userData.email}</Typography>
      <Typography sx={{ mb: 1 }}>Último acceso: {userData.lastLogin}</Typography>
      <Typography sx={{ mb: 1 }}>Cuenta creada: {userData.created}</Typography>
      <Typography sx={{ mb: 1 }}>Proveedores vinculados: {userData.providers}</Typography>
      <Divider sx={{ my: 2 }} />
      <Typography variant="subtitle1" sx={{ mb: 1 }}>Últimos 10 accesos:</Typography>
      <Box sx={{ mb: 2 }}>
        <button onClick={handleCerrarOtrasSesiones} style={{padding:'8px 16px',background:'#1976d2',color:'#fff',border:'none',borderRadius:4,cursor:'pointer',fontWeight:'bold'}}>Cerrar todas las sesiones menos la actual</button>
      </Box>
      {accesses.length === 0 && <Typography>No hay registros.</Typography>}
      {accesses.map((a, i) => (
        <Box key={i} sx={{ mb: 1, p: 1, background: brandColors.backgroundDefault, borderRadius: 1 }}>
          <Typography variant="body2">Fecha: {a.timestamp ? new Date(a.timestamp).toLocaleString() : 'Sin fecha'}</Typography>
          <Typography variant="body2">País: {a.country || 'Desconocido'}</Typography>
          {a.latitude && a.longitude && (
            <img
              alt="mapa"
              src={`https://static-maps.yandex.ru/1.x/?lang=es_ES&ll=${a.longitude},${a.latitude}&z=12&size=300,150&l=map&pt=${a.longitude},${a.latitude},pm2rdm`}
              style={{ width: 300, height: 150, borderRadius: 8, marginTop: 8 }}
            />
          )}
        </Box>
      ))}
    </Box>
  );
}

export default Settings;
